import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiSave, FiSend } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { getMySelfAssessment, saveMySelfAssessment } from "../../services/assessmentApi";
import useAsync from "../../hooks/useAsync";
import useFormDraft from "../../hooks/useFormDraft";
import DraftNotice from "../../components/common/DraftNotice";
import { Card, PageHeader, Button, Loader, ErrorState, Banner, FormField, StatusBadge, ConfirmDialog, EvidencePanel } from "../../components/common";
import ScoreBreakdown from "../../components/performance/ScoreBreakdown";
import AssessmentSections from "../../components/performance/AssessmentSections";
import { SELF_SECTION_MAP } from "../../services/core";
import { directionLabel } from "../../components/performance/KraKpiTable";
import { KpiRecordedHint } from "../../components/performance/KpiMeasurements";
import { computePerformance, itemAchievement } from "../../utils/scoreCalculator";
import { formatDateTime, formatScore } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";
import { ASSESSMENT_STATUS } from "../../utils/constants";

const api = { getMySelfAssessment, saveMySelfAssessment };

const toMap = (list, fields) => Object.fromEntries(list.map((x) => [x.id, Object.fromEntries(fields.map((f) => [f, x[f] ?? ""]))]));

export default function SelfAssessmentPage() {
  const { cycleId, cycle } = useCycle();
  const { data, error, reload } = useAsync(() => api.getMySelfAssessment(cycleId), [cycleId], { skip: !cycleId });
  const rating = { data: data?.config };

  const [form, setForm] = useState({ keyAchievements: "", strengths: "", challenges: "", developmentAreas: "" });
  const [goalRows, setGoalRows] = useState({});
  const [kraRows, setKraRows] = useState({});
  const [kpiRows, setKpiRows] = useState({});
  const [saving, setSaving] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!data) return;
    const a = data.assessment;
    const { status, submittedAt, ...texts } = a;
    setForm({ keyAchievements: "", strengths: "", challenges: "", developmentAreas: "", ...texts });
    setGoalRows(toMap(data.goals, ["achievement", "employeeComments"]));
    setKraRows(toMap(data.kras.filter((kra) => kra.assessmentInputRequired), ["actual", "employeeComments"]));
    setKpiRows(toMap(data.kpis, ["actual", "employeeComments"]));
  }, [data]);

  // [NEW] Everything typed on this page (texts + per goal/KRA/KPI rows) is kept in this browser until saved,
  // so a power cut, a closed tab or an urgent meeting does not lose the work.
  const pageText = useMemo(() => ({ form, goalRows, kraRows, kpiRows }), [form, goalRows, kraRows, kpiRows]);
  const setPageText = useCallback((v) => { setForm(v.form); setGoalRows(v.goalRows || {}); setKraRows(v.kraRows || {}); setKpiRows(v.kpiRows || {}); }, []);
  const editable = Boolean(data) && data.assessment.status !== ASSESSMENT_STATUS.SUBMITTED && data.stageOpen;
  const draft = useFormDraft(`self-assessment:${cycleId}`, pageText, setPageText, { active: editable });

  const preview = useMemo(() => {
    if (!data) return {};
    return computePerformance({
      goals: data.goals.map((g) => ({ ...g, achievement: goalRows[g.id]?.achievement, managerAchievement: null })),
      kras: data.kras.map((kra) => (kra.assessmentInputRequired ? { ...kra, actual: kraRows[kra.id]?.actual } : kra)),
      kpis: data.kpis.map((k) => ({ ...k, actual: kpiRows[k.id]?.actual })),
      config: rating.data,
    });
  }, [data, goalRows, kraRows, kpiRows]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const submitted = data.assessment.status === ASSESSMENT_STATUS.SUBMITTED;
  const readOnly = submitted || !data.stageOpen;
  const noGoals = data.goals.length === 0;

  const setRow = (setter) => (id, field) => (e) => setter((rows) => ({ ...rows, [id]: { ...rows[id], [field]: e.target.value } }));
  const setGoal = setRow(setGoalRows);
  const setKra = setRow(setKraRows);
  const setKpi = setRow(setKpiRows);

  const payload = () => ({
    ...form,
    goalUpdates: Object.entries(goalRows).map(([id, v]) => ({ id, ...v })),
    kraUpdates: Object.entries(kraRows).map(([id, v]) => ({ id, ...v })),
    kpiUpdates: Object.entries(kpiRows).map(([id, v]) => ({ id, ...v })),
  });

  const save = async (submit) => {
    setSaving(submit ? "submit" : "draft");
    try {
      await api.saveMySelfAssessment(cycleId, payload(), submit);
      draft.clear();
      toast.success(submit ? "Self assessment submitted to your manager." : "Draft saved.");
      setConfirm(false);
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Self assessment"
        subtitle={`${cycle?.name}. Rate your goals, record KPI actuals, and reflect on the year. KRA scores are derived from KPIs.`}
        actions={<StatusBadge status={data.assessment.status} />}
      />

      <DraftNotice draft={draft} />

      {submitted && (
        <Banner tone="success" title="Submitted">
          Sent on {formatDateTime(data.assessment.submittedAt)}. Your manager will review it next. Scores you entered are now read-only.
        </Banner>
      )}
      {!submitted && !data.stageOpen && (
        <Banner tone="neutral" title="The self assessment window is not open">You can view your entries, but changes are disabled until HR opens this stage.</Banner>
      )}
      {noGoals && (
        <Banner tone="warning" title="No approved goals yet" action={<Link className="link-btn" to="/my-goals">Go to my goals</Link>}>
          Your goals must be approved before you can submit a self assessment.
        </Banner>
      )}

      <div className="assessment-layout">
        <div className="stack">
          <Card title="1. Goal achievement" subtitle="Enter how much of each goal you achieved in %. Over-achievement above 100% is allowed where the cycle permits it.">
            <div className="line-table">
              {data.goals.map((g) => (
                <div key={g.id} className="line-row">
                  <div className="line-info">
                    <strong>{g.title}</strong>
                    <small>Goal {g.weightage}%. Target {g.target} {g.unit}.</small>
                  </div>
                  <FormField label="Achievement %" className="line-num" hint={rating.data?.scoreCap ? `Up to ${rating.data.scoreCap}%` : undefined}>
                    <input type="number" min="0" max={rating.data?.scoreCap || undefined} value={goalRows[g.id]?.achievement ?? ""} onChange={setGoal(g.id, "achievement")} disabled={readOnly} />
                  </FormField>
                  <FormField label="What you achieved" className="line-comment">
                    <input value={goalRows[g.id]?.employeeComments ?? ""} onChange={setGoal(g.id, "employeeComments")} disabled={readOnly} placeholder="Evidence or context" />
                  </FormField>
                </div>
              ))}
              {noGoals && <p className="muted">Nothing to rate yet.</p>}
            </div>
          </Card>

          <Card title="2. KRA results" subtitle="Each KRA result is calculated from the KPIs listed under it.">
            <div className="line-table">
              {data.kras.map((k) => {
                const derived = preview.kraBreakdown?.find((row) => String(row.kraItemId) === String(k.id));
                return (
                  <div key={k.id} className="line-row">
                    <div className="line-info">
                      <strong>{k.name}</strong>
                      <small>{k.assessmentInputRequired
                        ? `${k.measurementMethod}. Target ${k.target}. Weight ${k.weightage}%.`
                        : `${k.weightage}% KRA weight. Calculated from ${k.kpis?.length || 0} child KPI${k.kpis?.length === 1 ? "" : "s"}.`}</small>
                    </div>
                    {k.assessmentInputRequired ? (
                      <>
                        <FormField label="Actual" className="line-num" hint={`Achievement ${formatScore(itemAchievement({ ...k, actual: kraRows[k.id]?.actual }))}%`}>
                          <input type="number" min="0" step="any" value={kraRows[k.id]?.actual ?? ""} onChange={setKra(k.id, "actual")} disabled={readOnly} />
                        </FormField>
                        <FormField label="Comment" className="line-comment">
                          <input value={kraRows[k.id]?.employeeComments ?? ""} onChange={setKra(k.id, "employeeComments")} disabled={readOnly} />
                        </FormField>
                      </>
                    ) : (
                      <div className="line-num">
                        <span className="detail-label">Derived score</span>
                        <strong>{derived?.score == null ? "—" : `${formatScore(derived.score)}%`}</strong>
                      </div>
                    )}
                  </div>
                );
              })}
              {!data.kras.length && <p className="muted">No KRAs assigned for this cycle.</p>}
            </div>
          </Card>

          <Card title="3. KPI actuals" subtitle="Direction matters: for lower-is-better KPIs, a smaller number scores higher.">
            <div className="line-table">
              {data.kpis.map((k) => (
                <div key={k.id} className="line-row">
                  <div className="line-info">
                    <strong>{k.name}</strong>
                    <small>{directionLabel(k.direction)}. Target {k.target} {k.unit}.</small>
                    <KpiRecordedHint kpi={k} disabled={readOnly} onUse={(v) => setKpiRows((rows) => ({ ...rows, [k.id]: { ...rows[k.id], actual: String(v) } }))} />
                  </div>
                  <FormField label={`Actual (${k.unit})`} className="line-num" hint={`Achievement ${formatScore(itemAchievement({ ...k, actual: kpiRows[k.id]?.actual }, k.direction))}%`}>
                    <input type="number" min="0" step="any" value={kpiRows[k.id]?.actual ?? ""} onChange={setKpi(k.id, "actual")} disabled={readOnly} />
                  </FormField>
                  <FormField label="Comment" className="line-comment">
                    <input value={kpiRows[k.id]?.employeeComments ?? ""} onChange={setKpi(k.id, "employeeComments")} disabled={readOnly} />
                  </FormField>
                </div>
              ))}
              {!data.kpis.length && <p className="muted">No KPIs assigned for this cycle.</p>}
            </div>
          </Card>

          <EvidencePanel
            owner="performance"
            ownerId={data.recordId}
            canUpload={!readOnly}
            context="SELF_ASSESSMENT"
            title="4. Supporting evidence"
            linkItems={[
              ...data.goals.map((g) => ({ id: g.itemId, title: g.title, type: "GOAL" })),
              ...data.kras.map((k) => ({ id: k.itemId, title: k.name, type: "KRA" })),
              ...data.kpis.map((k) => ({ id: k.itemId, title: k.name, type: "KPI" })),
            ]}
          />

          <Card title="5. Reflection" subtitle="The sections HR chose for this cycle. All of them are required to submit.">
            <AssessmentSections
              allowed={data.sections}
              map={SELF_SECTION_MAP}
              labels={{
                keyAchievements: { label: "Key achievements", placeholder: "The results you are most proud of this cycle" },
                strengths: { label: "Strengths" },
                challenges: { label: "Challenges" },
                developmentAreas: { label: "Areas you want to develop" },
              }}
              form={form}
              setForm={setForm}
              readOnly={readOnly}
            />
          </Card>
        </div>

        <aside className="assessment-side">
          <Card>
            <ScoreBreakdown performance={preview} weights={rating.data?.weights} restricted={rating.data?.restricted} scoreCap={rating.data?.scoreCap} title="Your estimated score" provisional />
            <p className="footnote">Calculated by the system from your entries. Your manager can adjust goal achievement during review.</p>
            {!readOnly && (
              <div className="side-actions">
                <Button variant="secondary" icon={FiSave} block loading={saving === "draft"} disabled={Boolean(saving)} onClick={() => save(false)}>Save draft</Button>
                <Button icon={FiSend} block disabled={Boolean(saving) || noGoals} onClick={() => setConfirm(true)}>Submit to manager</Button>
              </div>
            )}
          </Card>
        </aside>
      </div>

      <ConfirmDialog
        open={confirm}
        title="Submit your self assessment?"
        message="After you submit, your entries are locked and your manager is notified."
        confirmLabel="Submit assessment"
        loading={saving === "submit"}
        onConfirm={() => save(true)}
        onClose={() => setConfirm(false)}
      />
    </div>
  );
}
